/**
 * Summer Hill — Wishlist
 * Client-only (localStorage) save-for-later. No app/backend required.
 *
 * Wire-up: add `data-wishlist-toggle="{{ product.handle }}"` to any button,
 * optionally with a child `[data-wishlist-heart]` (glyph) and/or
 * `[data-wishlist-label]` (text) to have them reflect saved state.
 * A `[data-wishlist-count]` element anywhere shows the saved item count.
 * A `[data-wishlist-page]` section with a `[data-wishlist-grid]` and
 * `[data-wishlist-empty]` inside it renders the full saved list.
 */
(function () {
  var KEY = 'sh_wishlist';

  function getList() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveList(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable (private mode, etc.) — fail silently */
    }
  }

  function isSaved(handle) {
    return getList().indexOf(handle) > -1;
  }

  function toggle(handle) {
    var list = getList();
    var i = list.indexOf(handle);
    if (i > -1) {
      list.splice(i, 1);
    } else {
      list.push(handle);
    }
    saveList(list);
    var saved = list.indexOf(handle) > -1;
    document.dispatchEvent(new CustomEvent('sh:wishlist-change'));
    return saved;
  }

  function updateCount() {
    var count = getList().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  function setButtonState(btn, saved) {
    btn.classList.toggle('is-active', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    var heart = btn.querySelector('[data-wishlist-heart]');
    if (heart) heart.textContent = saved ? '♥' : '♡';
    var label = btn.querySelector('[data-wishlist-label]');
    if (label) label.textContent = saved ? 'Saved to Wishlist' : 'Add to Wishlist';
  }

  function refreshButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      setButtonState(btn, isSaved(btn.getAttribute('data-wishlist-toggle')));
    });
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function cdnResize(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function buildCard(product) {
    var onSale = product.compare_at_price > product.price;

    var card = document.createElement('div');
    card.className = 'sh-wl__card';

    var mediaLink = document.createElement('a');
    mediaLink.href = product.url;
    mediaLink.className = 'sh-wl__card-media';
    if (product.featured_image) {
      var img = document.createElement('img');
      img.src = cdnResize(product.featured_image, 600);
      img.alt = product.title;
      img.loading = 'lazy';
      mediaLink.appendChild(img);
    }
    card.appendChild(mediaLink);

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'sh-wl__card-remove';
    removeBtn.setAttribute('data-wishlist-toggle', product.handle);
    removeBtn.setAttribute('aria-label', 'Remove from wishlist');
    var heart = document.createElement('span');
    heart.setAttribute('data-wishlist-heart', '');
    heart.textContent = '♥';
    removeBtn.appendChild(heart);
    card.appendChild(removeBtn);

    var nameLink = document.createElement('a');
    nameLink.href = product.url;
    nameLink.className = 'sh-wl__card-name';
    nameLink.textContent = product.title;
    card.appendChild(nameLink);

    var priceRow = document.createElement('div');
    priceRow.className = 'sh-wl__card-price' + (onSale ? ' is-sale' : '');
    priceRow.textContent = formatMoney(product.price, product.currency || card.dataset.currency);
    if (onSale) {
      var compare = document.createElement('span');
      compare.className = 'sh-wl__card-compare';
      compare.textContent = formatMoney(product.compare_at_price, product.currency || card.dataset.currency);
      priceRow.appendChild(document.createTextNode(' '));
      priceRow.appendChild(compare);
    }
    card.appendChild(priceRow);

    return card;
  }

  function renderWishlistPage() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    if (!grid) return;
    var currency = grid.getAttribute('data-currency') || 'USD';
    var handles = getList();

    if (handles.length === 0) {
      grid.innerHTML = '';
      grid.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    grid.hidden = false;
    grid.setAttribute('aria-busy', 'true');

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (products) {
      grid.innerHTML = '';
      products.forEach(function (product) {
        if (!product) return;
        product.currency = currency;
        grid.appendChild(buildCard(product));
      });
      grid.removeAttribute('aria-busy');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var saved = toggle(btn.getAttribute('data-wishlist-toggle'));
    setButtonState(btn, saved);
  });

  document.addEventListener('sh:wishlist-change', function () {
    refreshButtons();
    updateCount();
    renderWishlistPage();
  });

  document.addEventListener('DOMContentLoaded', function () {
    refreshButtons();
    updateCount();
    renderWishlistPage();
  });

  window.shWishlist = {
    getList: getList,
    isSaved: isSaved,
    toggle: toggle,
    updateCount: updateCount,
    refresh: refreshButtons
  };
})();
